/**
 * New / Edit alert rule — the dialog both apps open (the form is AlertRuleForm).
 *
 *   header   EDIT ALERT RULE · the rule's name · its severity · On / Off · ✕
 *   body     the questions, and beside them how the alert will read + what will be saved
 *   footer   "No changes yet" / "Unsaved changes" / what still stops a save · Cancel · Save
 *
 * The host owns the form state, validation and the save call (each app has its own API).
 */
import Modal from '../common/Modal';
import { AASwitch } from '../alertActions/AlertActionsControls';
import AlertRuleForm, { type AlertRuleFormProps } from './AlertRuleForm';

export interface AlertRuleModalProps extends AlertRuleFormProps {
  open: boolean;
  /** A new rule (Create) or an existing one (Save changes). */
  isNew: boolean;
  /** The form as it opened — "No changes yet" until it differs. */
  initial: Record<string, any> | null;
  saving: boolean;
  /** The last save's error. */
  error: string;
  /** What stops a save right now ('' = nothing). */
  problem: string;
  onSave: () => void;
  onClose: () => void;
  /** Bumped by the host on every open: the form remounts with this rule's own state. */
  formKey?: number;
}

const CHIP: Record<string, { label: string; cls: string; dot: string }> = {
  critical: { label: 'Critical', cls: 'bg-red-500/10 text-red-600', dot: 'bg-red-500' },
  warning: { label: 'Warning', cls: 'bg-amber-500/10 text-amber-600', dot: 'bg-amber-500' },
  normal: { label: 'Info', cls: 'bg-blue-500/10 text-blue-600', dot: 'bg-blue-500' },
  '*': { label: 'Auto', cls: 'bg-muted text-muted-foreground', dot: 'bg-muted-foreground' },
};

/** Same content whatever the key order (a patch can add keys the rule did not have). */
function stable(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stable).join(',')}]`;
  if (v && typeof v === 'object') {
    return `{${Object.keys(v as object).sort().filter((k) => (v as any)[k] !== undefined).map((k) => `${k}:${stable((v as any)[k])}`).join(',')}}`;
  }
  return JSON.stringify(v ?? null);
}

export default function AlertRuleModal({
  open, isNew, initial, saving, error, problem, onSave, onClose, formKey, ...formProps
}: AlertRuleModalProps) {
  const { form, setForm } = formProps;
  const chip = CHIP[form.severity || '*'] || CHIP['*'];
  const on = form.is_active !== false;
  const dirty = !!initial && stable(form) !== stable(initial);
  const status = error
    ? <span className="text-destructive">{error}</span>
    : problem && (dirty || isNew)
      ? <span className="text-destructive">{problem}</span>
      : dirty ? <span className="font-medium text-foreground">Unsaved changes</span> : <span>No changes yet</span>;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="wide"
      fillBody
      eyebrow={isNew ? 'New alert rule' : 'Edit alert rule'}
      ariaLabel={isNew ? 'New alert rule' : `Edit alert rule ${form.name || ''}`}
      title={(
        <span className="inline-flex max-w-full items-center gap-3">
          <span className="truncate">{form.name || (isNew ? 'Untitled rule' : 'Alert rule')}</span>
          <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${chip.cls}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${chip.dot}`} />
            {chip.label}
          </span>
        </span>
      )}
      headerActions={(
        <div className="mr-1 flex items-center gap-2 text-[13px] text-foreground">
          <AASwitch checked={on} onChange={(v) => setForm({ ...form, is_active: v })} label={on ? 'Turn this rule off' : 'Turn this rule on'} />
          <span className="w-6">{on ? 'On' : 'Off'}</span>
        </div>
      )}
      footer={(
        <div className="flex items-center gap-3">
          <p className="min-w-0 flex-1 truncate text-[13px] text-muted-foreground" title={typeof problem === 'string' ? problem : undefined}>{status}</p>
          <button type="button" onClick={onClose} disabled={saving}
            className="rounded-lg border border-border px-4 py-2 text-[13px] font-medium text-foreground hover:bg-muted disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={onSave} disabled={saving || !!problem || (!isNew && !dirty)}
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50">
            {saving && <span className="h-3.5 w-3.5 animate-spin rounded-full border-t-2 border-white" />}
            {saving ? 'Saving…' : isNew ? 'Create rule' : 'Save changes'}
          </button>
        </div>
      )}
    >
      <AlertRuleForm key={formKey} {...formProps} />
    </Modal>
  );
}
